/**
 * ホーム（スマホの幅）の部品の配置。管理UIの「ホームの司書」（/librarian）の見本で決めて公開する。
 * 形は管理UIの local-ui/src/types/home-layout.ts と同じ。正本は Firestore の appSettings/homeLayout（公開版）
 */
import type { LibraryIconName } from "@/components/ui/LibraryIcon";
import { NAV_DESTINATIONS, NAV_ICONS, type NavDestinationId } from "@/lib/nav-tabs";

export const HOME_PIECE_IDS = ["plaque", "level", "note", "continue", "gacha", "shelf", "map", "quests", "goals", "companions"] as const;
export type HomePieceId = (typeof HOME_PIECE_IDS)[number];

export interface HomePiecePlacement {
  /** 部品の中心の横位置。中身の幅（最大 448px）に対する割合 */
  x: number;
  /** 上半分の部品は画面の上端から、下半分の部品は下端から測る */
  anchor: "top" | "bottom";
  /** 上端（または下端）から部品の縁までの px */
  offset: number;
  /** 管理UIの見本で端を掴んで変えた幅（px）。無ければ HOME_PIECE_WIDTH */
  width?: number;
  /** 管理UIの見本で端を掴んで変えた高さ（px）。少なくともこの高さ（中身より低くはならない） */
  height?: number;
}

export type HomeLayoutPieces = Record<HomePieceId, HomePiecePlacement>;

/** 部品の幅（px）。管理UIの HOME_PIECES の width と同じ（390px 幅のスマホで測った値） */
export const HOME_PIECE_WIDTH: Record<HomePieceId, number> = {
  plaque: 208,
  level: 358,
  note: 208,
  continue: 358,
  gacha: 358,
  shelf: 175,
  map: 175,
  quests: 358,
  goals: 208,
  companions: 358,
};

/**
 * 後から足した部品の置き場所（公開済みの配置にその部品が無いとき）。管理UIの local-ui/src/types/home-layout.ts の MISSING_PIECE_PLACEMENT と同じ。
 * goals＝解放目標・ログインボーナスのしおり（10/12）。左の列の吹き出しの下
 */
export const MISSING_PIECE_PLACEMENT: Partial<Record<HomePieceId, HomePiecePlacement>> = {
  goals: { x: 0.291, anchor: "top", offset: 290 },
};

/**
 * 管理UIで変えた部品のアイコン・行き先（空の欄は今のまま）。部品の文は管理UIの「文言」（src/lib/ui-texts.ts の home.*）で決める。どの部品のどの欄を変えられるかは管理UIの HOME_PIECE_FIELDS が決める。
 * 中身そのもの（館の名前・未読の数・吹き出しの文など）はコードが決める
 */
export interface HomePieceCustom {
  /** "none" はアイコンを出さない */
  icon?: LibraryIconName | "none";
  destination?: NavDestinationId;
  view?: string;
}
export type HomePieceCustoms = Partial<Record<HomePieceId, HomePieceCustom>>;

export function parseHomeCustom(data: unknown): HomePieceCustoms {
  const custom = (data as { custom?: Record<string, unknown> } | undefined)?.custom;
  if (!custom || typeof custom !== "object") return {};
  const out: HomePieceCustoms = {};
  for (const id of HOME_PIECE_IDS) {
    const c = custom[id] as Record<string, unknown> | undefined;
    if (!c || typeof c !== "object") continue;
    const destination = typeof c.destination === "string" && c.destination in NAV_DESTINATIONS ? (c.destination as NavDestinationId) : undefined;
    out[id] = {
      icon: c.icon === "none" ? "none" : typeof c.icon === "string" && (NAV_ICONS as readonly string[]).includes(c.icon) ? (c.icon as LibraryIconName) : undefined,
      destination,
      view: destination && typeof c.view === "string" ? c.view : undefined,
    };
  }
  return out;
}

/** 管理UIで隠した部品（スマホの幅でも広い画面でも出さない）。読めない値は捨てる。配置を読めなくても効く */
export function parseHiddenPieces(data: unknown): HomePieceId[] {
  const hidden = (data as { hidden?: unknown } | undefined)?.hidden;
  if (!Array.isArray(hidden)) return [];
  return HOME_PIECE_IDS.filter((id) => hidden.includes(id));
}

/** Firestore の値を読む。部品が一つでも欠けていたり形が違えば null（ホームは今の並びのまま）。後から足した部品だけは、欠けていても MISSING_PIECE_PLACEMENT に置く */
export function parseHomeLayout(data: unknown): HomeLayoutPieces | null {
  const pieces = (data as { pieces?: Record<string, unknown> } | undefined)?.pieces;
  if (!pieces) return null;
  const out = {} as HomeLayoutPieces;
  for (const id of HOME_PIECE_IDS) {
    const p = (pieces[id] ?? MISSING_PIECE_PLACEMENT[id]) as Partial<HomePiecePlacement> | undefined;
    if (!p || typeof p.x !== "number" || typeof p.offset !== "number" || (p.anchor !== "top" && p.anchor !== "bottom")) return null;
    out[id] = {
      x: p.x,
      anchor: p.anchor,
      offset: p.offset,
      width: typeof p.width === "number" && p.width > 0 ? p.width : undefined,
      height: typeof p.height === "number" && p.height > 0 ? p.height : undefined,
    };
  }
  return out;
}
